import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useOrgId } from './org';

export interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  poBox: string | null;
  /** PIN code (India). */
  postalCode?: string | null;
}

export interface CompanySettings {
  id: string;
  company: {
    name: string;
    legalName: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
    address: Address;
    emirate: string | null;
    country: string;
  };
  vat: {
    vatRegistered: boolean;
    trn: string | null;
    vatRegistrationDate: string | null;
    taxPeriod: 'monthly' | 'quarterly';
    taxPack: string;
  };
  /** India GST registration; null for UAE clients. */
  gst?: {
    gstin: string | null;
    stateCode: string;
    registrationType: string;
    registrationDate: string | null;
    taxPeriod: 'monthly' | 'quarterly';
  } | null;
  sales: {
    defaultTermsDays: number;
    invoicePrefix: string;
    invoiceMessage: string | null;
    statementMessage: string | null;
    showSku: boolean;
    emailSubject: string;
    emailMessage: string;
    emailCc: string | null;
  };
  expenses: { defaultBillTermsDays: number; showItemsTable: boolean };
  advanced: {
    fiscalYearStart: number;
    canChangeFiscalYearStart: boolean;
    baseCurrency: string;
    chartTemplate: string | null;
    timeZone: string;
    dateFormat: string;
    enableAccountNumbers: boolean;
    warnDuplicateInvoiceNumbers: boolean;
    warnDuplicateBillNumbers: boolean;
    /** Classes, locations and projects (Advanced › Categories, Projects). */
    trackClasses?: 'off' | 'per_line' | 'per_transaction';
    trackLocations?: boolean;
    locationLabel?: string;
    useProjects?: boolean;
  };
  updatedAt: string;
}

export type SettingsPatch = {
  [K in 'company' | 'vat' | 'gst' | 'sales' | 'expenses' | 'advanced']?: Record<string, unknown>;
};

export function useCompanySettings() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['company-settings', orgId],
    queryFn: () => api<CompanySettings>(`/orgs/${orgId}/company-settings`),
    enabled: Boolean(orgId),
  });
}

export function useSaveSettings() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SettingsPatch) =>
      api<CompanySettings>(`/orgs/${orgId}/company-settings`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: async (saved) => {
      qc.setQueryData(['company-settings', orgId], saved);
      // the client switcher and Clients list show the name, emirate and TRN
      await qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
    },
  });
}

export const EMIRATES = [
  'Abu Dhabi',
  'Dubai',
  'Sharjah',
  'Ajman',
  'Umm Al Quwain',
  'Ras Al Khaimah',
  'Fujairah',
] as const;

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** QBO "Terms" list: due on receipt or net n days. */
export const TERMS: Array<{ days: number; label: string }> = [
  { days: 0, label: 'Due on receipt' },
  { days: 7, label: 'Net 7' },
  { days: 15, label: 'Net 15' },
  { days: 30, label: 'Net 30' },
  { days: 45, label: 'Net 45' },
  { days: 60, label: 'Net 60' },
  { days: 90, label: 'Net 90' },
];

export const termsLabel = (days: number): string =>
  TERMS.find((t) => t.days === days)?.label ?? `Net ${days}`;
